import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, Loader2, Send, Euro, Calendar, Briefcase, FileText, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ApplicationModal = ({ mission, isOpen, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    cover_letter: '',
    proposed_rate: '',
    availability_date: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cover_letter.trim()) {
      toast.error('Veuillez rédiger une lettre de motivation');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('sfretire_token');
      
      const payload = {
        mission_id: mission.id,
        cover_letter: formData.cover_letter,
        proposed_rate: formData.proposed_rate ? parseFloat(formData.proposed_rate) : null,
        availability_date: formData.availability_date || null,
      };

      const response = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitted(true);
        // Reset form after short delay
        setTimeout(() => {
          setFormData({
            cover_letter: '',
            proposed_rate: '',
            availability_date: '',
          });
          setSubmitted(false);
          onSuccess && onSuccess(result.application);
        }, 2000);
      } else {
        toast.error(result.detail || 'Erreur lors de la candidature');
      }
    } catch (error) {
      console.error('Application error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !mission) return null;

  // Success state
  if (submitted) {
    return (
      <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card rounded-2xl shadow-elevated w-full max-w-md p-8 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircle className="text-green-600" size={32} />
          </motion.div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Candidature envoyée !
          </h3>
          <p className="text-muted-foreground">
            Votre candidature a été transmise à l&apos;entreprise. Vous serez notifié dès qu&apos;elle sera étudiée.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl shadow-elevated w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Send className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-serif font-semibold text-foreground">
                Postuler
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-1">
                {mission.title}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Mission info summary */}
          <div className="bg-secondary/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Briefcase className="text-primary flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-medium text-foreground">{mission.title}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {mission.sector && `${mission.sector} • `}
                  {mission.location && `${mission.location} • `}
                  {mission.work_mode === 'remote' ? 'Télétravail' : mission.work_mode === 'hybrid' ? 'Hybride' : 'Sur site'}
                </p>
                {mission.budget_max && (
                  <p className="text-sm font-medium text-primary mt-1">
                    Budget: {mission.budget_min && `${mission.budget_min} - `}{mission.budget_max}€
                    /{mission.rate_type === 'hourly' ? 'h' : mission.rate_type === 'daily' ? 'jour' : 'forfait'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Cover Letter */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <FileText size={16} className="text-muted-foreground" />
              Lettre de motivation *
            </label>
            <Textarea
              name="cover_letter"
              value={formData.cover_letter}
              onChange={handleChange}
              placeholder="Présentez-vous et expliquez pourquoi vous êtes le candidat idéal pour cette mission..."
              className="mt-1 min-h-[150px]"
              data-testid="application-cover-letter"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Mettez en avant votre expérience pertinente et votre valeur ajoutée
            </p>
          </div>

          {/* Proposed Rate */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Euro size={16} className="text-muted-foreground" />
              Tarif proposé (optionnel)
            </label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                name="proposed_rate"
                value={formData.proposed_rate}
                onChange={handleChange}
                placeholder="Ex: 500"
                min="0"
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm">
                €/{mission.rate_type === 'hourly' ? 'heure' : mission.rate_type === 'daily' ? 'jour' : 'forfait'}
              </span>
            </div>
          </div>

          {/* Availability */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <Calendar size={16} className="text-muted-foreground" />
              Date de disponibilité (optionnel)
            </label>
            <Input
              type="date"
              name="availability_date"
              value={formData.availability_date}
              onChange={handleChange}
              className="mt-1"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border bg-secondary/30">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="gold"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1"
            data-testid="submit-application-btn"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Send className="mr-2" size={16} />
            )}
            Envoyer ma candidature
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default ApplicationModal;
